import { useFormContext } from "react-hook-form";
import { classes } from "../../../../../util/style-utils";
import { useMemo } from "react";
import CentersOfOperationUpload from "./CentersOfOperationUpload";
import AddressFields from "../../../../AddressFields";
import useYesNoOptions from "../../../../../hooks/useYesNoOptions";
import RadioButtonGroup from "../../../../gds/RadioButtonGroup";
import SelectBox from "../../../../gds/SelectBox";
import { useMask } from "@react-input/mask";
import "@gdk/base/dist/js/jquery.maskedinput.geico";
import { AddressLabelsWithRequiredIndicator } from "../../../../../constants/AddressFieldLabels";
import useCobrandedAgency from "../../../hooks/useCobrandedAgency";

export default function CentersOfOperation() {
    const yesNoOptions = useYesNoOptions();
    const { isCobrandedAgency } = useCobrandedAgency();

    const { register, watch, formState: { errors }, control } = useFormContext();

    const centersOfOperationOptions = [
        { value: "one", label: "One" },
        { value: "two", label: "Two" },
        { value: "threeOrMore", label: "Three or more" }
    ];

    const watchCentersOfOperationOutsidePrimaryLocation = watch("centersOfOperationOutsidePrimaryLocation");
    const watchNumberOfCentersOfOperationUnderSameEin = watch("numberOfCentersOfOperationUnderSameEin");

    const showBranch1Fields = useMemo(() => (
        watchCentersOfOperationOutsidePrimaryLocation === "true" &&
        (
            watchNumberOfCentersOfOperationUnderSameEin === "one" ||
            watchNumberOfCentersOfOperationUnderSameEin === "two"
        )
    ), [watchCentersOfOperationOutsidePrimaryLocation, watchNumberOfCentersOfOperationUnderSameEin]);

    const showBranch2Fields = useMemo(() => (
        watchCentersOfOperationOutsidePrimaryLocation === "true" &&
        watchNumberOfCentersOfOperationUnderSameEin === "two"
    ), [watchCentersOfOperationOutsidePrimaryLocation, watchNumberOfCentersOfOperationUnderSameEin]);

    const showUpload = useMemo(() => (
        watchCentersOfOperationOutsidePrimaryLocation === "true" &&
        watchNumberOfCentersOfOperationUnderSameEin === "threeOrMore"
    ), [watchCentersOfOperationOutsidePrimaryLocation, watchNumberOfCentersOfOperationUnderSameEin]);

    const { ref: registerBranchPhoneRef, ...registerBranchPhone } = register("branchPhone");
    const branchPhoneMaskRef = useMask({ mask: '(___) ___-____', replacement: { _: /\d/ } });

    const { ref: registerBranch2PhoneRef, ...registerBranch2Phone } = register("branch2Phone");
    const branch2PhoneMaskRef = useMask({ mask: '(___) ___-____', replacement: { _: /\d/ } });

    // Hide entire Centers of Operation section for cobranded agencies
    // (centersOfOperationOutsidePrimaryLocation is defaulted to "No")
    if (isCobrandedAgency) {
        return null;
    }

    return <>
        <h3 className="margin-top margin-bottom">Centers of Operation</h3>

        <RadioButtonGroup trackingId="PrincipalFlow:AgencyOfficeInformation:CentersOfOperation:OutsidePrimaryLocation"
            control={control}
            name="centersOfOperationOutsidePrimaryLocation"
            legend={<>
                Do you have any Centers of Operation outside of your primary location?{/*
                */}<span className='required'>*</span>
            </>}
            layout="Two Column"
            options={yesNoOptions}
        />

        {watchCentersOfOperationOutsidePrimaryLocation === "true" &&
            <SelectBox
                control={control}
                name="numberOfCentersOfOperationUnderSameEin"
                label={<>
                    How many Centers of Operation do you have conducting business under the same EIN?{/*
                    */}<span className='required'>*</span>
                </>}
                placeholder="Please Select"
                options={centersOfOperationOptions}
            />
        }

        {showBranch1Fields &&
            <>
                <span className="stroke-separator"></span>
                <div className='flex-row'>
                    <div className={classes("form-field", errors.branchName && "form-field--error")}>
                        <label htmlFor="branch-name" className="text">
                            Branch Name{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input
                            id="branch-name"
                            type="text"
                            {...register("branchName")}
                        />
                        {errors.branchName &&
                            <span className="form-message">{errors.branchName.message}</span>
                        }
                    </div>
                </div>

                <AddressFields
                    fieldNames={{
                        line1: "branchAddressLine1",
                        line2: "branchAddressLine2",
                        city: "branchAddressCity",
                        state: "branchAddressState",
                        zipCode: "branchAddressZipCode"
                    }}
                    labels={AddressLabelsWithRequiredIndicator}
                />

                <div className='flex-row'>
                    <div className={classes("form-field", errors.branchContactFirstName && "form-field--error")}>
                        <label htmlFor="branch-contact-first-name" className="text">
                            Branch Contact First Name{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input id="branch-contact-first-name" type="text" {...register("branchContactFirstName")} />
                        {errors.branchContactFirstName &&
                            <span className="form-message">{errors.branchContactFirstName.message}</span>
                        }
                    </div>

                    <div className={classes("form-field", errors.branchContactLastName && "form-field--error")}>
                        <label htmlFor="branch-contact-last-name" className="text">
                            Branch Contact Last Name{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input id="branch-contact-last-name" type="text" {...register("branchContactLastName")} />
                        {errors.branchContactLastName &&
                            <span className="form-message">{errors.branchContactLastName.message}</span>
                        }
                    </div>
                </div>

                <div className='flex-row'>
                    <div className={classes("form-field", errors.branchContactEmail && "form-field--error")}>
                        <label htmlFor="branch-contact-email" className="text">
                            Branch Contact Email{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input id='branch-contact-email' type="email" {...register("branchContactEmail")} />
                        {errors.branchContactEmail &&
                            <span className="form-message">{errors.branchContactEmail.message}</span>
                        }
                    </div>

                    <div className={classes("form-field", errors.branchContactRole && "form-field--error")}>
                        <label htmlFor="branch-contact-role" className="text">
                            Branch Contact Role{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input id='branch-contact-role' type="text" {...register("branchContactRole")} />
                        {errors.branchContactRole &&
                            <span className="form-message">{errors.branchContactRole.message}</span>
                        }
                    </div>
                </div>

                <div className='flex-row'>
                    <div className={classes("form-field", errors.branchPhone && "form-field--error")}>
                        <label htmlFor="branch-phone" className="text">
                            Branch Office Phone Number{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input
                            id="branch-phone"
                            type="text"
                            className="phone-number"
                            ref={(e) => {
                                if (registerBranchPhoneRef) registerBranchPhoneRef(e);
                                branchPhoneMaskRef.current = e;
                            }}
                            {...registerBranchPhone}
                        />
                        {errors.branchPhone &&
                            <span className="form-message">{errors.branchPhone.message}</span>
                        }
                    </div>
                </div>
            </>
        }

        {showBranch2Fields &&
            <>
                <span className="stroke-separator"></span>
                <div className='flex-row'>
                    <div className={classes("form-field", errors.branch2Name && "form-field--error")}>
                        <label htmlFor="branch2-name" className="text">
                            Branch Name{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input
                            id="branch2-name"
                            type="text"
                            {...register("branch2Name")}
                        />
                        {errors.branch2Name &&
                            <span className="form-message">{errors.branch2Name.message}</span>
                        }
                    </div>
                </div>

                <AddressFields
                    fieldNames={{
                        line1: "branch2AddressLine1",
                        line2: "branch2AddressLine2",
                        city: "branch2AddressCity",
                        state: "branch2AddressState",
                        zipCode: "branch2AddressZipCode"
                    }}
                    labels={AddressLabelsWithRequiredIndicator}
                />

                <div className='flex-row'>
                    <div className={classes("form-field", errors.branch2ContactFirstName && "form-field--error")}>
                        <label htmlFor="branch2-contact-first-name" className="text">
                            Branch Contact First Name{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input id="branch2-contact-first-name" type="text" {...register("branch2ContactFirstName")} />
                        {errors.branch2ContactFirstName &&
                            <span className="form-message">{errors.branch2ContactFirstName.message}</span>
                        }
                    </div>

                    <div className={classes("form-field", errors.branch2ContactLastName && "form-field--error")}>
                        <label htmlFor="branch2-contact-last-name" className="text">
                            Branch Contact Last Name{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input id="branch2-contact-last-name" type="text" {...register("branch2ContactLastName")} />
                        {errors.branch2ContactLastName &&
                            <span className="form-message">{errors.branch2ContactLastName.message}</span>
                        }
                    </div>
                </div>

                <div className='flex-row'>
                    <div className={classes("form-field", errors.branch2ContactEmail && "form-field--error")}>
                        <label htmlFor="branch2-contact-email" className="text">
                            Branch Contact Email{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input id='branch2-contact-email' type="email" {...register("branch2ContactEmail")} />
                        {errors.branch2ContactEmail &&
                            <span className="form-message">{errors.branch2ContactEmail.message}</span>
                        }
                    </div>

                    <div className={classes("form-field", errors.branch2ContactRole && "form-field--error")}>
                        <label htmlFor="branch2-contact-role" className="text">
                            Branch Contact Role{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input id='branch2-contact-role' type="text" {...register("branch2ContactRole")} />
                        {errors.branch2ContactRole &&
                            <span className="form-message">{errors.branch2ContactRole.message}</span>
                        }
                    </div>
                </div>

                <div className='flex-row'>
                    <div className={classes("form-field", errors.branch2Phone && "form-field--error")}>
                        <label htmlFor="branch2-phone" className="text">
                            Branch Office Phone Number{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input
                            id="branch2-phone"
                            type="text"
                            className="phone-number"
                            ref={(e) => {
                                if (registerBranch2PhoneRef) registerBranch2PhoneRef(e);
                                branch2PhoneMaskRef.current = e;
                            }}
                            {...registerBranch2Phone}
                        />
                        {errors.branch2Phone &&
                            <span className="form-message">{errors.branch2Phone.message}</span>
                        }
                    </div>
                </div>
            </>
        }

        {showUpload &&
            <>
                <h3>Centers of Operation</h3>

                <p>
                    Upload your Centers of Operation using our <strong>Centers of Operation Template</strong> below.
                </p>

                <ol className="list list--numbered bold-markers">
                    <li>
                        <strong>Download</strong> the Centers of Operation Template-using another template can lead to upload errors
                    </li>
                    <li>
                        <strong>Complete</strong> the list and save it as a .csv file
                        <ul className="square-markers">
                            <li>
                                List the branch name, address, and contact information for each Center of Operation
                            </li>
                        </ul>
                    </li>
                    <li>
                        <strong>Upload</strong> the completed file
                    </li>
                    <li>
                        <strong>Submit</strong> the file
                    </li>
                </ol>

                <div>
                    <a data-onboarding-link-id="PrincipalFlow:AgencyOfficeInformation:CentersOfOperation:DownloadTemplate" href="geao/centers-of-operation.csv" target="_blank">
                        <button data-onboarding-button-id="PrincipalFlow:AgencyOfficeInformation:CentersOfOperation:UploadCentersOfOperation" type="button" className="btn btn--primary btn--full-mobile">
                            <span>Download Centers of Operation Template</span>
                        </button>
                    </a>
                </div>

                <strong>Upload only one .csv file. Max 25MB.</strong>

                <CentersOfOperationUpload />
            </>
        }
    </>
}